import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const KINDS = [
  { id: "sponsor", label: "Sponsorship Inquiry", description: "Underwriting an interview, series, or salon." },
  { id: "press", label: "Press & Media", description: "Editorial requests, embargoed previews, syndication." },
  { id: "guest_nomination", label: "Featured Guest Nomination", description: "Suggest a magnate, CEO, or family-office steward." },
  { id: "brand_partnership", label: "Brand Partnership", description: "Heritage houses, luxury goods, private services." },
  { id: "general", label: "General Inquiry", description: "Anything else worth our editorial desk's attention." },
];

const initialForm = {
  kind: "general",
  name: "",
  email: "",
  organization: "",
  role: "",
  website: "",
  message: "",
};

export default function Partners() {
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast.error("Please share a bit more in your message (at least 10 characters).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/partners", form);
      setSubmitted(true);
      toast.success("Your inquiry has been received.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="partners-submitted" className="min-h-[80vh] flex items-center justify-center px-6 pt-24 pb-12">
        <div className="max-w-xl text-center reveal">
          <CheckCircle2 size={48} style={{ color: "var(--brass)", margin: "0 auto 1.5rem" }} />
          <p className="eyebrow mb-4">Received</p>
          <h1 className="font-display text-5xl md:text-6xl mb-6 leading-[0.95]">
            Thank you, <em style={{ color: "var(--brass)" }}>{form.name.split(" ")[0] || "friend"}</em>.
          </h1>
          <p className="text-lg leading-relaxed mb-8" style={{ color: "var(--ink-muted)" }}>
            Our editorial desk reviews every inquiry personally. We will respond to you in due course —
            typically within five business days.
          </p>
          <button
            onClick={() => { setSubmitted(false); setForm(initialForm); }}
            className="btn-ghost"
            data-testid="partners-new-inquiry"
          >
            Submit Another Inquiry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="partners-page" className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-32 pb-24">
      <div className="mb-16 reveal">
        <p className="eyebrow mb-4">By Invitation & Inquiry</p>
        <h1 className="font-display text-6xl md:text-7xl mb-6 leading-[0.95]">
          Partners, Press, <br />& <em style={{ color: "var(--brass)" }}>Nominations</em>.
        </h1>
        <p className="text-lg max-w-2xl leading-relaxed" style={{ color: "var(--ink-muted)" }}>
          Wealth First Inc. is supported by its members and by a small, carefully chosen circle of partners.
          Whether you wish to underwrite an interview series, nominate a magnate worthy of our archive, or
          propose a brand collaboration — write to us here. The editorial desk reads every line.
        </p>
      </div>

      <hr className="hairline mb-12" />

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="partners-form">
        {/* Kind selector */}
        <div className="lg:col-span-5">
          <p className="eyebrow mb-5">Nature of Inquiry</p>
          <div className="space-y-3">
            {KINDS.map((k) => (
              <label
                key={k.id}
                data-testid={`kind-${k.id}`}
                className="flex gap-4 p-4 cursor-pointer transition-all duration-300"
                style={{
                  border: form.kind === k.id ? "1px solid var(--brass)" : "1px solid var(--hairline)",
                  background: form.kind === k.id ? "var(--surface)" : "transparent",
                }}
              >
                <input
                  type="radio"
                  name="kind"
                  value={k.id}
                  checked={form.kind === k.id}
                  onChange={() => set("kind", k.id)}
                  className="mt-1 accent-[var(--brass)]"
                />
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.15em]" style={{ color: form.kind === k.id ? "var(--brass)" : "var(--ink)" }}>
                    {k.label}
                  </p>
                  <p className="text-xs mt-1 leading-relaxed" style={{ color: "var(--ink-muted)" }}>
                    {k.description}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Fields */}
        <div className="lg:col-span-7 space-y-7">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
            <div>
              <label className="eyebrow block mb-2">Your Name *</label>
              <input
                required
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className="input-line"
                data-testid="partners-name"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">Email *</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="input-line"
                data-testid="partners-email"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">Organization</label>
              <input
                value={form.organization}
                onChange={(e) => set("organization", e.target.value)}
                className="input-line"
                data-testid="partners-organization"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">Role / Title</label>
              <input
                value={form.role}
                onChange={(e) => set("role", e.target.value)}
                className="input-line"
                data-testid="partners-role"
              />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-2">Website</label>
            <input
              value={form.website}
              onChange={(e) => set("website", e.target.value)}
              placeholder="https://"
              className="input-line"
              data-testid="partners-website"
            />
          </div>
          <div>
            <label className="eyebrow block mb-2">Your Message *</label>
            <textarea
              required
              minLength={10}
              rows={7}
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              placeholder="Tell us, in your own words, what you have in mind."
              className="w-full bg-transparent p-3 text-base outline-none transition-colors"
              style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink)", fontFamily: "Manrope" }}
              data-testid="partners-message"
            />
          </div>
          <div className="flex justify-between items-center pt-2">
            <p className="text-xs" style={{ color: "var(--ink-faint)" }}>
              We respond within five business days.
            </p>
            <button type="submit" disabled={busy} className="btn-brass" data-testid="partners-submit">
              {busy ? "Sending…" : "Submit Inquiry"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
